import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch, RootState } from "../store";
import { receiptActions } from "../slices/receipt";
import { getErrorMessage } from "../../utils/errorUtils";
import { ProductStub } from "./invoiceActions";

import { ReceiptRequestDto } from "../../shared/dtos/receipt/ReceiptRequestDto";
import { ReceiptResponseDto } from "../../shared/dtos/receipt/ReceiptResponseDto";
import { ReceiptSummaryDto } from "../../shared/dtos/receipt/ReceiptSummaryDto";
import { DateRangePaginationQueryDto } from "../../shared/dtos/common/DateRangePaginationQueryDto";
import { EmailRequestDto } from "../../shared/dtos/common/EmailRequestDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";
import { SaleType } from "../../shared/enums/SaleType";

export const getReceipts =
  (params?: DateRangePaginationQueryDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(receiptActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<ReceiptSummaryDto>>
      >("/receipts", {
        headers: { requiresAuth: true },
        params,
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(receiptActions.setItems(data.data.content));
        dispatch(receiptActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(receiptActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getReceiptById =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(receiptActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<ReceiptResponseDto>
      >(`/receipts/${id}`, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(receiptActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(receiptActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const createReceipt =
  (payload: ReceiptRequestDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(receiptActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<ReceiptResponseDto>
      >("/receipts", payload, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Sale completed successfully!");
        dispatch(receiptActions.clearPosCart());
        dispatch(
          receiptActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(receiptActions.setErrorCreate(message));
    }
  };

export const getReceiptPdf =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(receiptActions.setLoadingPdf());
    try {
      const { data } = await axiosInstance.get<Blob>(`/receipts/pdf/${id}`, {
        headers: { requiresAuth: true },
        responseType: "blob",
      });

      const pdfBlobUrl = window.URL.createObjectURL(
        new Blob([data], { type: "application/pdf" }),
      );
      dispatch(receiptActions.setPdfUrl(pdfBlobUrl));
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error("Failed to load PDF preview: " + message);
      dispatch(receiptActions.setErrorPdf(message));
    }
  };

export const emailReceipt =
  (id: string, emailPayload: EmailRequestDto) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    dispatch(receiptActions.setLoadingEmail());
    try {
      const { data } = await axiosInstance.post<ApiResponseDto<void>>(
        `/receipts/email/${id}`,
        emailPayload,
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Receipt emailed successfully!");
        dispatch(receiptActions.setEmailSuccess());
        return true;
      }
      return false;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(receiptActions.setErrorEmail(message));
      return false;
    }
  };

export const resetReceiptServerStatus =
  () =>
  (dispatch: AppDispatch): void => {
    dispatch(receiptActions.setServerStatus(null));
  };

// --- Cart Actions ---
export const addReceiptItem =
  (product: ProductStub, qty = 1) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    const { saleType } = getState().receipt;
    const price =
      saleType === SaleType.WHOLESALE
        ? product.wholesalePrice
        : product.retailPrice;

    dispatch(
      receiptActions.posItemAdd({
        itemId: product.id,
        name: product.name,
        qty: qty,
        price: price,
        discount: 0,
        brand: product.brandName,
        category: product.categoryName,
      }),
    );
  };

export const updateCartItemFields =
  (itemId: string, updates: any) =>
  (dispatch: AppDispatch): void => {
    dispatch(receiptActions.posItemUpdateFields({ itemId, updates }));
  };

export const removeReceiptItem =
  (itemId: string) =>
  (dispatch: AppDispatch): void => {
    dispatch(receiptActions.posItemRemove(itemId));
  };

export const changeSaleType =
  (newType: SaleType) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    const { cartItems } = getState().receipt;
    if (cartItems.length > 0) {
      toast.error(
        "Cannot switch sale types when there are items in the receipt. Please clear the receipt first.",
      );
      return;
    }
    dispatch(receiptActions.setPosSaleType(newType));
    toast.success(`Sale type changed to ${newType}`);
  };

export const resetReceipt =
  () =>
  (dispatch: AppDispatch): void => {
    dispatch(receiptActions.clearPosCart());
  };
